import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Box, H5, Divider } from "orcs-design-system";
import { useHistory, useLocation } from "react-router-dom";
import { useQuery } from "@apollo/client/react";
import { get, isEmpty } from "lodash";
import TreeViewNext from "src/shared/TreeViewNext";
import urlPaths, { url, tagIdFromUrl } from "src/consts/urlPaths";
import TagTreeHeader from "src/components/TagsNavigation/TagTreeHeader";
import { getExploreTagTreeLinkTo } from "src/components/TagsNavigation/getTagTreeLinkTo";
import { pagedSearchTags, getTagDetails } from "src/queries/tags.graphql";
import { TAG_LOAD_LIMIT } from "src/util/consts";
import { getUniqueTags } from "src/util/tags";
import { useWorkspaceTagConfig } from "src/contexts/global/WorkspaceContext";
import { isTagType } from "src/util/tagType";
import { isTagRouteActive } from "src/util/tagRouteActive";
import useLazyLoad from "src/hooks/useLazyLoad";
import { reportError } from "src/services/errorReporting";
import type { GetTagDetailsQueryData } from "src/types/tagsGraphql";
import buildTagTypeTree, {
  type TagTypeNode,
  type TagData,
} from "./buildTagTypeTree";
import useAutoExpandActiveTag from "./useAutoExpandActiveTag";
import { isActiveInTagTypeSection } from "./resolveTagExpandPath";

type TagType = {
  id: string;
  name: string;
};

type TagTypeNavigationProps = {
  tagType: TagType;
  showAppliedCount?: boolean;
};

const TagTypeNavigation = ({
  tagType,
  showAppliedCount = false,
}: TagTypeNavigationProps) => {
  const [treeData, setTreeData] = useState<TagTypeNode[]>([]);
  const [childrenLoaded, setChildrenLoaded] = useState<Record<string, boolean>>(
    {}
  );
  const [allTags, setAllTags] = useState<Record<string, TagData>>({});
  const [toggledNodes, setToggledNodes] = useState<Record<string, boolean>>({});
  const loadedCountRef = useRef<Record<string, number>>({});
  const allTagsRef = useRef(allTags);
  const userCollapsedIdsRef = useRef(new Set<string>());
  allTagsRef.current = allTags;
  const location = useLocation();
  const activeTagId = tagIdFromUrl(location.pathname);
  const history = useHistory();
  const tagTypesConfig = useWorkspaceTagConfig() as Record<string, unknown>;

  const isActiveTagTypeId = !!(
    activeTagId && isTagType(tagTypesConfig, activeTagId)
  );

  const { data: tagDetailsData } = useQuery<GetTagDetailsQueryData>(
    getTagDetails,
    {
      variables: {
        tagId: activeTagId,
        showAppliedCount,
      },
      skip: !activeTagId || isActiveTagTypeId,
    }
  );

  const activeTag = isActiveTagTypeId
    ? undefined
    : tagDetailsData?.results?.tags?.[0];

  const sectionIsActive = isActiveInTagTypeSection(
    activeTagId,
    tagType.id,
    tagTypesConfig,
    activeTag
  );

  useEffect(() => {
    userCollapsedIdsRef.current.clear();
  }, [activeTagId]);

  const mergeActiveTag = useCallback(
    (tag: { id: string; type?: string; displayValue?: string }) => {
      const tagTypeKey = tag.type;
      if (!tagTypeKey) {
        return;
      }

      setAllTags((prev) => ({
        ...prev,
        [tagTypeKey]: {
          ...prev[tagTypeKey],
          tags: getUniqueTags([
            ...(prev[tagTypeKey]?.tags ?? []),
            tag,
          ] as unknown[]) as unknown as TagData["tags"],
        },
      }));
    },
    []
  );

  const getTagLinkTo = useCallback(
    (node: TagTypeNode) =>
      getExploreTagTreeLinkTo(node, {
        tagTypesConfig,
        childrenLoaded,
        childTagsByNodeId: allTags,
      }),
    [tagTypesConfig, childrenLoaded, allTags]
  );

  const loadTags = useLazyLoad({
    query: pagedSearchTags,
    additionalVariables: { showAppliedCount },
  });

  const loadChildTags = useCallback(
    async (node: TagTypeNode): Promise<TagData | null> => {
      const { id: nodeId, name } = node;
      if (!nodeId) {
        return null;
      }

      const currentTags = allTagsRef.current[nodeId]?.tags ?? [];
      const offset = loadedCountRef.current[nodeId] ?? 0;
      const filterInput = isTagType(tagTypesConfig, nodeId)
        ? { tagTypes: [nodeId] }
        : { attribute: { type: "parentTag", value: nodeId } };

      try {
        const data = await loadTags({
          input: { filter: filterInput, offset, limit: TAG_LOAD_LIMIT },
        });

        const newTags =
          (data as { results?: { tags?: unknown[] } })?.results?.tags ?? [];
        const totalCount =
          (data as { results?: { totalCount?: number } })?.results
            ?.totalCount ?? 0;

        loadedCountRef.current[nodeId] = offset + newTags.length;

        const merged: TagData = {
          tags: getUniqueTags([
            ...(currentTags as unknown[]),
            ...newTags,
          ]) as unknown as TagData["tags"],
          totalCount,
          name: isTagType(tagTypesConfig, nodeId)
            ? (get(tagTypesConfig, [nodeId, "name"], nodeId) as string)
            : name,
        };

        setAllTags((prev) => ({ ...prev, [nodeId]: merged }));
        setChildrenLoaded((prev) => ({ ...prev, [nodeId]: true }));

        return merged;
      } catch (error) {
        reportError(error, {
          context: "TagTypeNavigation",
          action: "loadChildTags",
          nodeId,
        });
        return null;
      }
    },
    [loadTags, tagTypesConfig]
  );

  useAutoExpandActiveTag({
    tagTypeId: tagType.id,
    tagTypeName: tagType.name,
    activeTagId,
    activeTag,
    tagTypesConfig,
    childrenLoaded,
    loadChildTags,
    setToggledNodes,
    mergeActiveTag,
  });

  useEffect(() => {
    if (!tagType?.id) {
      return;
    }

    const data = buildTagTypeTree({
      nodeId: tagType.id,
      tags: allTags,
      activeTagId,
      node: tagType,
      childrenLoaded,
      toggledNodes,
    });

    setTreeData(data);
  }, [
    activeTagId,
    allTags,
    tagType,
    tagTypesConfig,
    toggledNodes,
    childrenLoaded,
  ]);

  const isTagNodeActive = useCallback(
    (node: TagTypeNode) =>
      sectionIsActive &&
      isTagRouteActive(node, activeTagId, tagTypesConfig, tagType.id),
    [activeTagId, sectionIsActive, tagType.id, tagTypesConfig]
  );

  const openStateStructureKey = useMemo(() => {
    const collectKeys = (node: TagTypeNode): string[] => {
      const open = toggledNodes[node.id] ?? false;
      const childKeys = (node.children ?? []).flatMap(collectKeys);
      return [`${node.id}:${open}`, ...childKeys];
    };
    return treeData.flatMap(collectKeys).join("|");
  }, [treeData, toggledNodes]);

  const customizeTreeHeader = useCallback(
    ({ node }: { node: TagTypeNode }, helpers: { onActivate: () => void }) => (
      <TagTreeHeader
        node={node}
        getTagLinkTo={getTagLinkTo}
        navigateTagTypeWithHistory={false}
        onActivate={() => helpers.onActivate()}
      />
    ),
    [getTagLinkTo]
  );

  const onChevronToggle = useCallback((node: TagTypeNode, isOpen: boolean) => {
    if (isOpen) {
      userCollapsedIdsRef.current.delete(node.id);
    } else {
      userCollapsedIdsRef.current.add(node.id);
    }
    setToggledNodes((prev) => ({ ...prev, [node.id]: isOpen }));
  }, []);

  const getNodeOpenState = useCallback(
    (node: TagTypeNode) => {
      if (userCollapsedIdsRef.current.has(node.id)) {
        return false;
      }
      return toggledNodes[node.id] ?? false;
    },
    [toggledNodes]
  );

  const onNodeSelected = useCallback(
    async (node: TagTypeNode) => {
      const { id } = node;
      userCollapsedIdsRef.current.delete(id);
      setToggledNodes((prev) => ({ ...prev, [id]: true }));

      const result = childrenLoaded[id]
        ? allTags[id]
        : await loadChildTags(node);
      const hasTags = !isEmpty(result?.tags);

      if (!hasTags && !isTagType(tagTypesConfig, id)) {
        history.push(url(urlPaths.TAG_DETAILS, { id }));
      } else {
        history.push(url(urlPaths.TAG_EXPLORE, { id }));
      }
    },
    [history, loadChildTags, tagTypesConfig, childrenLoaded, allTags]
  );

  return (
    <Box data-testid={`${tagType.id}-navigation`}>
      <H5 fontWeight="bold" data-testid="heading">
        {tagType.name}
      </H5>
      <Box my="s">
        <Divider light />
      </Box>
      {!treeData.length && <H5>No tags data available</H5>}
      <TreeViewNext<TagTypeNode>
        data={treeData}
        onNodeSelected={onNodeSelected}
        onChevronToggle={onChevronToggle}
        customizeTreeHeader={customizeTreeHeader}
        activeNodeId={sectionIsActive ? activeTagId : undefined}
        isNodeActive={isTagNodeActive}
        getNodeOpenState={getNodeOpenState}
        openStateStructureKey={openStateStructureKey}
        syncOpenStateOnDataChange={false}
        openByDefault={false}
        suppressOnNodeSelectedWhenCollapsingBranch
        scrollActiveNodeOnDataChange={sectionIsActive}
        isNodeDisabled={(node) => !!node.disabled}
      />
    </Box>
  );
};

export default TagTypeNavigation;
